import React from 'react';
import { 
  FileText, 
  Download, 
  BookOpen,
  HelpCircle,
  Users,
  Map,
  Droplets,
  Calendar,
  AlertTriangle,
  Settings,
  Shield,
  Wrench,
  Play,
  Video
} from 'lucide-react';

const Documentation: React.FC = () => {
  const chapters = [
    { id: 1, title: 'Introduction', icon: BookOpen, description: 'Présentation de la plateforme Agri Safe' },
    { id: 2, title: 'Première Connexion', icon: Users, description: 'Comment accéder et se connecter' },
    { id: 3, title: 'Tableau de Bord', icon: FileText, description: 'Vue d\'ensemble et KPIs' },
    { id: 4, title: 'Carte Interactive', icon: Map, description: 'Navigation cartographique' },
    { id: 5, title: 'Gestion des Zones', icon: Droplets, description: 'Surveillance des zones d\'irrigation' },
    { id: 6, title: 'Appareils IoT', icon: Wrench, description: 'Inventaire et état des équipements' },
    { id: 7, title: 'Système d\'Alertes', icon: AlertTriangle, description: 'Gestion des alertes et notifications' },
    { id: 8, title: 'Planification', icon: Calendar, description: 'Programmes d\'irrigation et fertigation' },
    { id: 9, title: 'Ordres de Travail', icon: FileText, description: 'Gestion des interventions' },
    { id: 10, title: 'Paramètres', icon: Settings, description: 'Configuration du système' },
    { id: 11, title: 'Administration', icon: Shield, description: 'Gestion Super Admin' },
    { id: 12, title: 'FAQ & Dépannage', icon: HelpCircle, description: 'Questions fréquentes et solutions' },
  ];

  const handleDownloadPDF = () => {
    window.open('/docs/Guide-Utilisateur-AgriSafe.pdf', '_blank');
  };

  const handleDownloadMarkdown = () => {
    window.open('/docs/guide-utilisateur-agrisafe.md', '_blank');
  };

  const handleDownloadVideo = () => {
    window.open('/docs/video/AgriSafe-Walkthrough.mp4', '_blank');
  };

  const handlePlayVideo = () => {
    const modal = document.createElement('div');
    modal.id = 'video-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    `;
    modal.innerHTML = `
      <div style="position: relative; width: 90%; max-width: 1200px;">
        <button onclick="document.getElementById('video-modal').remove()" 
          style="position: absolute; top: -40px; right: 0; background: white; border: none; 
          width: 36px; height: 36px; border-radius: 50%; cursor: pointer; font-size: 20px;">
          ✕
        </button>
        <video controls autoplay style="width: 100%; border-radius: 8px;">
          <source src="/docs/video/AgriSafe-Walkthrough.mp4" type="video/mp4">
        </video>
      </div>
    `;
    modal.onclick = (e) => {
      if (e.target === modal) modal.remove();
    };
    document.body.appendChild(modal);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">📘 Guide d'Utilisation</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Documentation complète pour la prise en main de la plateforme Agri Safe
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button 
            onClick={handleDownloadPDF}
            className="btn-primary flex items-center space-x-2"
            data-testid="download-pdf"
          >
            <Download className="w-4 h-4" />
            <span>PDF</span>
          </button>
          <button 
            onClick={handleDownloadMarkdown}
            className="btn-secondary flex items-center space-x-2"
            data-testid="download-md"
          >
            <FileText className="w-4 h-4" />
            <span>Texte</span>
          </button>
          <button 
            onClick={handlePlayVideo}
            className="flex items-center space-x-2 px-4 py-2 bg-[var(--color-danger)] text-white rounded-lg hover:opacity-90 transition-opacity"
            data-testid="play-video"
          >
            <Play className="w-4 h-4" />
            <span>Vidéo</span>
          </button>
        </div>
      </div>

      {/* Video Walkthrough Banner */}
      <div className="card" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-[var(--color-danger)] rounded-xl flex items-center justify-center">
              <Video className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">🎬 Vidéo Walkthrough</h2>
              <p className="text-white text-opacity-80">
                Visite guidée complète de la plateforme • ~2 minutes
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={handlePlayVideo}
              className="flex items-center space-x-2 px-6 py-3 bg-[var(--color-danger)] text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
            >
              <Play className="w-5 h-5" />
              <span>Regarder</span>
            </button>
            <button 
              onClick={handleDownloadVideo}
              className="flex items-center space-x-2 px-6 py-3 bg-white bg-opacity-20 text-white font-semibold rounded-lg hover:bg-opacity-30 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>Télécharger MP4</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Chapitres', value: '12', color: 'var(--color-primary)' },
          { label: 'Pages PDF', value: '28', color: 'var(--color-info)' },
          { label: 'Images', value: '5', color: 'var(--color-warning)' },
          { label: 'Vidéo', value: '2 min', color: 'var(--color-danger)' },
          { label: 'Version', value: '1.0', color: 'var(--color-success)' },
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Download Banner */}
      <div className="card bg-gradient-primary text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Guide Complet en PDF</h2>
              <p className="text-white text-opacity-90">
                24 pages • Tous les chapitres • Illustrations et tableaux
              </p>
            </div>
          </div>
          <button 
            onClick={handleDownloadPDF}
            className="flex items-center space-x-2 px-6 py-3 bg-white text-[var(--color-primary)] font-semibold rounded-lg hover:bg-opacity-90 transition-colors"
          >
            <Download className="w-5 h-5" />
            <span>Télécharger le PDF</span>
          </button>
        </div>
      </div>

      {/* Chapters Grid */}
      <div>
        <h2 className="text-heading-3 text-[var(--text-primary)] mb-4">Table des Matières</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {chapters.map((chapter) => {
            const Icon = chapter.icon;
            return (
              <div 
                key={chapter.id}
                className="card hover:shadow-lg transition-all cursor-pointer"
                data-testid={`chapter-${chapter.id}`}
              >
                <div className="flex items-start space-x-4">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: 'var(--color-primary-100)' }}
                  >
                    <Icon className="w-5 h-5 text-[var(--color-primary)]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-xs font-medium text-[var(--text-tertiary)]">
                        Chapitre {chapter.id}
                      </span>
                    </div>
                    <h3 className="text-heading-4 text-[var(--text-primary)] mb-1">
                      {chapter.title}
                    </h3>
                    <p className="text-body-sm text-[var(--text-secondary)]">
                      {chapter.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Reference */}
      <div className="card">
        <h2 className="text-heading-3 text-[var(--text-primary)] mb-4">🔐 Identifiants de Connexion</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--border-light)]">
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">Type de Compte</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">Mot de passe</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-[var(--text-primary)]">Accès</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-danger)] text-white rounded-full">
                    Super Admin
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">admin@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">admin123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">Accès complet + Administration</td>
              </tr>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-primary)] text-white rounded-full">
                    Admin Coop
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">coop@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">coop123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">Gestion organisation</td>
              </tr>
              <tr className="border-b border-[var(--border-light)] hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-info)] text-white rounded-full">
                    Agriculteur
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">farmer@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">farmer123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">Visualisation et gestion basique</td>
              </tr>
              <tr className="hover:bg-[var(--gray-50)]">
                <td className="py-3 px-4">
                  <span className="px-2 py-1 text-xs bg-[var(--color-warning)] text-white rounded-full">
                    Technicien
                  </span>
                </td>
                <td className="py-3 px-4 font-mono text-sm">tech@agrisafe.fr</td>
                <td className="py-3 px-4 font-mono text-sm">tech123</td>
                <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">Maintenance et interventions</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Support Section */}
      <div className="card bg-[var(--gray-50)]">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-[var(--color-info-100)] rounded-lg flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-[var(--color-info)]" />
          </div>
          <div>
            <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">Besoin d'aide ?</h3>
            <p className="text-body-sm text-[var(--text-secondary)] mb-3">
              Si vous avez des questions ou rencontrez des difficultés, notre équipe support est là pour vous aider.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="text-[var(--text-secondary)]">
                📧 support@agrisafe.fr
              </span>
              <span className="text-[var(--text-secondary)]">
                📞 +33 4 67 XX XX XX
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
